/**
 * Microsoft Clarity — session recording & heatmaps.
 *
 * Behavior:
 *  - Production-only (skipped on lovable.app preview / localhost).
 *  - Skipped on /admin routes (internal tooling shouldn't be recorded).
 *  - Initialized once per page load.
 *  - PII masking enforced via Clarity dashboard settings + `data-clarity-mask`
 *    / `data-clarity-unmask` attributes on inputs (see usage in forms).
 *  - Users identified by internal Supabase user ID only — never email.
 *
 * Project ID is a public identifier (ships in client JS regardless), same as
 * the GA tag in index.html. Safe to commit.
 */

const CLARITY_PROJECT_ID = "wf8a8g7sxy";

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

let initialized = false;

function shouldLoadClarity(): boolean {
  if (typeof window === "undefined") return false;
  if (initialized) return false;

  // Production only — skip preview & local dev.
  const host = window.location.hostname;
  const isProd = host === "askthepods.com" || host === "www.askthepods.com";
  if (!isProd) return false;

  // Skip admin / internal routes.
  if (window.location.pathname.startsWith("/admin")) return false;

  return true;
}

/**
 * Initialize Microsoft Clarity. Safe to call multiple times — only loads once.
 * === CLARITY INIT ===
 */
export function initClarity(): void {
  if (!shouldLoadClarity()) return;
  initialized = true;

  // Inlined Clarity loader snippet (from clarity.microsoft.com).
  const c = window as unknown as Record<string, ClarityFn>;
  c.clarity =
    c.clarity ||
    (((...args: unknown[]) => {
      (c.clarity.q = c.clarity.q || []).push(args);
    }) as ClarityFn);
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://www.clarity.ms/tag/" + CLARITY_PROJECT_ID;
  const y = document.getElementsByTagName("script")[0];
  y.parentNode?.insertBefore(t, y);
}

/**
 * Identify the signed-in user to Clarity by internal user ID only.
 * NEVER pass email, name, or any other PII.
 */
export function identifyClarityUser(userId: string | null | undefined): void {
  if (!userId) return;

  // Dev-only safety log: verifies we only ever pass the Supabase UUID,
  // never an email or other PII. Stripped automatically in production
  // (hostname check matches initClarity).
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isProd = host === "askthepods.com" || host === "www.askthepods.com";
  if (!isProd) {
    const looksLikeEmail = /@/.test(userId);
    const looksLikeUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
    if (looksLikeEmail) {
      // eslint-disable-next-line no-console
      console.error(
        "[Clarity] BLOCKED identify() — value looks like an email. PII must never be sent to Clarity.",
      );
      return;
    }
    // eslint-disable-next-line no-console
    console.debug(
      `[Clarity] identify() called with ${looksLikeUuid ? "Supabase user ID (UUID ✓)" : "non-UUID id"}:`,
      userId,
    );
  }

  if (typeof window === "undefined" || !window.clarity) return;
  try {
    window.clarity("identify", userId);
    window.clarity("set", "user_id", userId);
  } catch {
    // no-op — Clarity should never break the app
  }
}
